// src/components/core/PwaInstallPrompt.jsx
// *******************************************************************************
// PWA INSTALL PROMPT
// Shows a bottom banner on mobile when the app is not installed as a PWA.
// Android/Chrome: uses the beforeinstallprompt event for one-tap install.
// iOS Safari: shows manual instructions (share → Add to Home Screen).
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import { useEffect, useRef, useState } from 'react';
import { Button, Flex, Typography } from 'antd';
import { CloseOutlined, DownloadOutlined } from '@ant-design/icons';
import translateX from 'utils/translateX';
import app from 'configs/appResolver';

const { Text } = Typography;

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const STORAGE_KEY = 'pwa_install_dismissed';
const DISMISS_DAYS = 7; // Show again after 7 days if dismissed

// *******************************************************************************
// HELPERS
// *******************************************************************************
function isMobileDevice() {
  return /android|iphone|ipad|ipod|mobile/i.test(navigator.userAgent);
}

function isIos() {
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

function isIosSafari() {
  const ua = navigator.userAgent;
  // iOS Safari does NOT have CriOS (Chrome), FxiOS (Firefox), EdgiOS (Edge), etc.
  return isIos() && /safari/i.test(ua) && !/crios|fxios|edgios|opios/i.test(ua);
}

function isIosChrome() {
  return isIos() && /crios/i.test(navigator.userAgent);
}

function isInStandaloneMode() {
  return (
    window.navigator.standalone === true ||
    window.matchMedia('(display-mode: standalone)').matches
  );
}

function isDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const { until } = JSON.parse(raw);
    return Date.now() < until;
  } catch {
    return false;
  }
}

function saveDismissed() {
  try {
    const until = Date.now() + DISMISS_DAYS * 24 * 60 * 60 * 1000;
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ until }));
  } catch {
    // ignore
  }
}

// *******************************************************************************
// iOS SHARE ICON (square with up arrow — matches Safari share button)
// *******************************************************************************
function IosShareIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 50 50"
      fill="none"
      stroke="#007AFF"
      strokeWidth="4"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ verticalAlign: 'middle', marginBottom: 2 }}
    >
      <polyline points="17 18 25 10 33 18" />
      <line x1="25" y1="10" x2="25" y2="36" />
      <path d="M15 24H12a2 2 0 00-2 2v16a2 2 0 002 2h26a2 2 0 002-2V26a2 2 0 00-2-2h-3" />
    </svg>
  );
}

// *******************************************************************************
// COMPONENT
// *******************************************************************************
const PwaInstallPrompt = () => {
  // ─────────────────────────────────────────────────────────────────────────────
  // 1. PROPS, CONTEXTS & GLOBAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const appName = app?.PWA_NAME || app?.NAME || 'App';
  const appIcon = `/apps/${app?.ID}/web-app-manifest-192x192.png`;

  // ─────────────────────────────────────────────────────────────────────────────
  // 2. HOOKS & REFS
  // ─────────────────────────────────────────────────────────────────────────────
  const deferredPromptRef = useRef(null);

  // ─────────────────────────────────────────────────────────────────────────────
  // 3. LOCAL STATE
  // ─────────────────────────────────────────────────────────────────────────────
  const [visible, setVisible] = useState(false);
  const [isIosDevice, setIsIosDevice] = useState(false);
  const [iosBrowser, setIosBrowser] = useState(null); // 'safari', 'chrome', or 'other'

  // ─────────────────────────────────────────────────────────────────────────────
  // 4. EFFECTS
  // ─────────────────────────────────────────────────────────────────────────────
  useEffect(() => {
    // Only show on mobile and when not already installed
    if (!isMobileDevice() || isInStandaloneMode() || isDismissed()) return;

    const ios = isIos();
    setIsIosDevice(ios);

    if (ios) {
      // Detect which browser on iOS
      if (isIosSafari()) {
        setIosBrowser('safari');
      } else if (isIosChrome()) {
        setIosBrowser('chrome');
      } else {
        setIosBrowser('other');
      }
      // iOS does not fire beforeinstallprompt — show manual instructions immediately
      setVisible(true);
      return;
    }

    // Android/Chrome: wait for the browser install prompt event
    const handler = e => {
      e.preventDefault();
      deferredPromptRef.current = e;
      setVisible(true);
    };

    window.addEventListener('beforeinstallprompt', handler);

    // Hide when installed
    const installedHandler = () => setVisible(false);
    window.addEventListener('appinstalled', installedHandler);

    return () => {
      window.removeEventListener('beforeinstallprompt', handler);
      window.removeEventListener('appinstalled', installedHandler);
    };
  }, []);

  // ─────────────────────────────────────────────────────────────────────────────
  // 5. HANDLERS
  // ─────────────────────────────────────────────────────────────────────────────
  const handleInstall = async () => {
    if (!deferredPromptRef.current) return;
    deferredPromptRef.current.prompt();
    const { outcome } = await deferredPromptRef.current.userChoice;
    deferredPromptRef.current = null;
    if (outcome === 'accepted') {
      setVisible(false);
    }
  };

  const handleDismiss = () => {
    saveDismissed();
    setVisible(false);
  };

  // ─────────────────────────────────────────────────────────────────────────────
  // 6. UI
  // ─────────────────────────────────────────────────────────────────────────────
  if (!visible) return null;

  return (
    <div
      style={{
        position: 'fixed',
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: '#fff',
        borderTop: '1px solid #f0f0f0',
        boxShadow: '0 -4px 16px rgba(0,0,0,0.12)',
        padding: '12px 16px',
      }}
    >
      <Flex align="center" gap={12}>
        {/* App icon */}
        <img
          src={appIcon}
          alt={appName}
          width={48}
          height={48}
          style={{ borderRadius: 12, flexShrink: 0 }}
        />

        {/* Message */}
        <Flex vertical flex={1} gap={2}>
          <Text strong style={{ fontSize: 14, lineHeight: '1.3' }}>
            {translateX('pwa_install_title', { appName })}
          </Text>
          {isIosDevice ? (
            iosBrowser === 'safari' ? (
              <div style={{ fontSize: 12, color: 'rgba(0,0,0,0.45)' }}>
                <div>
                  1. {translateX('pwa_install_ios_hint')}{' '}
                  <IosShareIcon />{' '}
                  {translateX('pwa_install_ios_step1')}
                </div>
                <div>
                  2. {translateX('pwa_install_ios_step2')}{' '}
                  <strong>{translateX('pwa_install_ios_add')}</strong>
                </div>
              </div>
            ) : (
              <Text type="secondary" style={{ fontSize: 12 }}>
                {translateX('pwa_install_ios_open_safari')}
              </Text>
            )
          ) : (
            <Text type="secondary" style={{ fontSize: 12 }}>
              {translateX('pwa_install_subtitle')}
            </Text>
          )}
        </Flex>

        {/* Actions */}
        <Flex gap={8} align="center" style={{ flexShrink: 0 }}>
          {!isIosDevice && (
            <Button
              type="primary"
              size="small"
              icon={<DownloadOutlined />}
              onClick={handleInstall}
              style={{ background: '#ff5757', borderColor: '#ff5757' }}
            >
              {translateX('install')}
            </Button>
          )}
          <Button
            type="text"
            size="small"
            icon={<CloseOutlined />}
            onClick={handleDismiss}
          />
        </Flex>
      </Flex>
    </div>
  );
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default PwaInstallPrompt;
